import { useEffect, useRef } from 'react'

function BackgroundPreview({ backgroundId }) {
  const containerRef = useRef(null)
  const styleRef = useRef(null)
  const scriptRef = useRef(null)
  const previewContainerRef = useRef(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    let cancelled = false
    const timeouts = []
    const later = (fn, ms) => {
      const id = window.setTimeout(() => {
        if (!cancelled) fn()
      }, ms)
      timeouts.push(id)
      return id
    }

    // Nettoyer uniquement les éléments de cette instance spécifique
    if (styleRef.current) {
      styleRef.current.remove()
      styleRef.current = null
    }
    if (scriptRef.current) {
      scriptRef.current.remove()
      scriptRef.current = null
    }
    if (previewContainerRef.current) {
      previewContainerRef.current.remove()
      previewContainerRef.current = null
    }
    container.innerHTML = ''

    if (backgroundId === 'default') {
      // Pour le fond par défaut, utiliser le CSS existant
      return
    }

    // Mapping des backgrounds vers leurs fichiers
    const backgroundMap = {
      'bubble-float': {
        folder: 'Bubble Float',
        css: 'Bubble Float.compiled.css',
        html: 'Bubble Float.html'
      },
      'shooting-stars': {
        folder: 'Shooting stars',
        css: 'Shooting stars.css',
        html: 'Shooting stars.html'
      },
      'gradient-waves': {
        folder: 'Gradient background with waves',
        css: 'Gradient background with waves.css',
        html: 'Gradient background with waves.html'
      },
      'foil-stamping': {
        folder: 'Foil Stamping Effect',
        css: 'Foil Stamping Effect.fixed.css',
        html: 'Foil Stamping Effect.fixed.html'
      },
      'flashlight-pattern': {
        folder: 'Animated Flashlight Pattern Backgroundexternal',
        css: 'Animated Flashlight Pattern Backgroundexternal.css',
        html: 'Animated Flashlight Pattern Backgroundexternal.html'
      },
      'day-sunset-night': {
        folder: 'Day-Sunset-Night Cycle',
        css: 'css.css',
        html: 'index.html',
        js: 'script.js'
      },
      'windows-11-colorful': {
        type: 'image',
        folder: 'Windows Wallpapers',
        image: 'windows-11-stock-official-colorful-3840x2160-5658.jpg'
      },
      'windows-11-dark-abstract': {
        type: 'image',
        folder: 'Windows Wallpapers',
        image: 'windows-11-dark-mode-abstract-background-black-background-3840x2160-8710.jpg'
      },
      'anime-town-rainfall': {
        type: 'video',
        folder: 'Windows Wallpapers',
        video: 'anime-town-rainfall.1920x1080.mp4'
      },
      'backyard': {
        type: 'video',
        folder: 'Windows Wallpapers',
        video: 'backyard.1920x1080.mp4'
      },
      'lofi-coffee-shop': {
        type: 'video',
        folder: 'Windows Wallpapers',
        video: 'lofi-coffee-shop.1920x1080.mp4'
      }
    }

    const bgConfig = backgroundMap[backgroundId]
    if (!bgConfig) return

    // Si c'est un background de type vidéo, charger directement la vidéo
    if (bgConfig.type === 'video') {
      const video = document.createElement('video')
      const videoFormats = Array.isArray(bgConfig.video) ? bgConfig.video : [bgConfig.video || 'video.mp4']
      videoFormats.forEach(format => {
        const source = document.createElement('source')
        source.src = `/backgrounds/${bgConfig.folder}/${format}`
        source.type = `video/${format.split('.').pop()}`
        video.appendChild(source)
      })
      
      video.autoplay = true
      video.loop = true
      video.muted = true
      video.playsInline = true
      video.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        border-radius: 8px;
      `
      
      video.onerror = () => console.error('Erreur chargement vidéo preview:', video.src)
      container.appendChild(video)
      
      // Vérifier que la vidéo est toujours dans le DOM avant de jouer
      // et gérer l'AbortError qui peut survenir si le composant est démonté
      video.play().catch(err => {
        // Ne pas afficher l'erreur si c'est une AbortError (composant démonté)
        // ou si la vidéo n'est plus dans le DOM
        if (err.name !== 'AbortError' && container.contains(video)) {
          console.error('Erreur lecture vidéo preview:', err)
        }
      })
      return
    }

    // Si c'est un background de type image, charger directement l'image
    if (bgConfig.type === 'image') {
      const img = document.createElement('img')
      img.src = `/backgrounds/${bgConfig.folder}/${bgConfig.image}`
      
      // Pour les GIFs, utiliser un meilleur algorithme de rendu
      if (bgConfig.image.endsWith('.gif')) {
        img.style.cssText = `
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          border-radius: 8px;
          image-rendering: auto;
          image-rendering: -webkit-optimize-contrast;
          image-rendering: smooth;
          -ms-interpolation-mode: bicubic;
          transform: translateZ(0);
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        `
      } else {
        img.style.cssText = `
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          border-radius: 8px;
          image-rendering: -webkit-optimize-contrast;
          image-rendering: crisp-edges;
          image-rendering: high-quality;
          -ms-interpolation-mode: bicubic;
        `
      }
      
      img.onerror = () => console.error('Erreur chargement image preview:', img.src)
      container.appendChild(img)
      return
    }

    // Créer un conteneur pour le preview avec un ID unique
    const previewContainer = document.createElement('div')
    const uniqueId = `preview-${backgroundId}-${Date.now()}`
    previewContainer.className = 'background-preview-container'
    previewContainer.setAttribute('data-preview-id', uniqueId)
    // FORCER l'affichage avec des styles inline directs
    previewContainer.style.position = 'absolute'
    previewContainer.style.top = '0'
    previewContainer.style.left = '0'
    previewContainer.style.width = '100%'
    previewContainer.style.height = '100%'
    previewContainer.style.overflow = 'hidden'
    previewContainer.style.borderRadius = '8px'
    previewContainer.style.background = '#1a1e23'
    previewContainer.style.display = 'block'
    previewContainer.style.visibility = 'visible'
    previewContainer.style.opacity = '1'
    previewContainer.style.zIndex = '1'
    container.appendChild(previewContainer)
    previewContainerRef.current = previewContainer

    // Sauvegarder l'ID unique pour le nettoyage
    const currentUniqueId = uniqueId

    // Fonction pour charger le HTML et extraire le CSS si nécessaire
    const loadHTMLAndExtractCSS = () => {
      if (!bgConfig.html) return Promise.resolve()

      const htmlPath = `/backgrounds/${bgConfig.folder}/${bgConfig.html}`
      return fetch(htmlPath)
        .then(response => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`)
          return response.text()
        })
        .then(htmlText => {
          // Vérifier que le conteneur existe toujours
          if (!previewContainerRef.current || !container.contains(previewContainerRef.current)) {
            return
          }

          const parser = new DOMParser()
          const doc = parser.parseFromString(htmlText, 'text/html')

          // Extraire le CSS des balises <style> dans le <head>
          const styleTags = doc.querySelectorAll('head style')
          let extractedCss = ''
          styleTags.forEach(styleTag => {
            extractedCss += styleTag.textContent + '\n'
          })

          // Si on a du CSS extrait, l'adapter et l'injecter
          if (extractedCss) {
            const style = document.createElement('style')
            style.setAttribute('data-background-preview', backgroundId)
            style.setAttribute('data-preview-unique-id', currentUniqueId)
            style.id = `preview-style-${currentUniqueId}`
            const uniqueSelector = `[data-preview-id="${currentUniqueId}"]`
            
            let finalCss = extractedCss
              .replace(/#custom-background-container\s+\./g, `${uniqueSelector} .`)
              .replace(/#custom-background-container\s+/g, `${uniqueSelector} `)
              .replace(/#custom-background-container>/g, `${uniqueSelector} >`)
              .replace(/#custom-background-container\./g, `${uniqueSelector}.`)
              .replace(/#custom-background-container#/g, `${uniqueSelector}#`)
              .replace(/#custom-background-container\[/g, `${uniqueSelector}[`)
              .replace(/#custom-background-container:/g, `${uniqueSelector}:`)
              .replace(/#custom-background-container{/g, `${uniqueSelector} {`)
              .replace(/#custom-background-container,/g, `${uniqueSelector},`)
              .replace(/#custom-background-container$/gm, uniqueSelector)
              .replace(/^body\s*{/gm, `${uniqueSelector} {`)
              .replace(/\sbody\s*{/g, ` ${uniqueSelector} {`)
              .replace(/position:\s*fixed/g, 'position: absolute')
              .replace(/100vw/g, '100%')
              .replace(/100vh/g, '100%')

            if (!finalCss.includes(uniqueSelector)) {
              finalCss = `${uniqueSelector} { position: absolute; top: 0; left: 0; width: 100%; height: 100%; overflow: hidden; background: #1a1e23; }\n${finalCss}`
            }


            style.textContent = finalCss
            document.head.appendChild(style)
            styleRef.current = style
          }

          // Extraire le contenu du body
          let bodyContent = doc.body.innerHTML || htmlText
          if (htmlText.includes('<body>')) {
            const bodyMatch = htmlText.match(/<body[^>]*>([\s\S]*?)<\/body>/i)
            if (bodyMatch) {
              bodyContent = bodyMatch[1]
            }
          }

          // Injecter le HTML
          previewContainer.innerHTML = bodyContent
          previewContainer.style.display = 'block'
          previewContainer.style.visibility = 'visible'
          previewContainer.style.opacity = '1'

          // Charger le JS si disponible
          if (bgConfig.js) {
            const jsPath = `/backgrounds/${bgConfig.folder}/${bgConfig.js}`
            fetch(jsPath)
              .then(response => response.text())
              .then(jsText => {
                // Adapter le script pour qu'il fonctionne dans le preview
                let adaptedJs = jsText
                  // Remplacer les sélecteurs pour trouver les vidéos dans le preview
                  .replace(/document\.getElementById\('video(\d+)'\)/g, (match, num) => {
                    return `document.querySelector('[data-preview-id="${currentUniqueId}"] #video${num}')`
                  })
                  .replace(/document\.getElementById\('video-source(\d+)'\)/g, (match, num) => {
                    return `document.querySelector('[data-preview-id="${currentUniqueId}"] #video-source${num}')`
                  })

                const script = document.createElement('script')
                script.textContent = adaptedJs
                later(() => {
                  document.head.appendChild(script)
                  scriptRef.current = script
                }, 100)
              })
              .catch(err => console.error('Erreur chargement JS preview:', err))
          }

          // Masquer l'indicateur de chargement
          const loadingIndicator = container.querySelector('.preview-loading-indicator')
          if (loadingIndicator) {
            loadingIndicator.style.display = 'none'
          }
        })
        .catch(err => {
          console.error('Erreur chargement HTML preview:', err)
          if (previewContainerRef.current) {
            previewContainerRef.current.innerHTML = `<div style="color: rgba(255,255,255,0.7); padding: 20px; text-align: center;">Erreur: ${err.message}</div>`
          }
        })
    }

    // Charger le CSS si disponible, sinon charger directement le HTML
    if (bgConfig.css) {
      const cssPath = `/backgrounds/${bgConfig.folder}/${bgConfig.css}`
      fetch(cssPath)
        .then(response => response.text())
        .then(cssText => {
        // Vérifier que le conteneur existe toujours (au cas où le composant serait démonté)
        if (!previewContainerRef.current || !container.contains(previewContainerRef.current)) {
          return
        }
        
        const style = document.createElement('style')
        style.setAttribute('data-background-preview', backgroundId)
        style.setAttribute('data-preview-unique-id', currentUniqueId)
        style.id = `preview-style-${currentUniqueId}`
        // Adapter le CSS pour le conteneur de prévisualisation avec un sélecteur unique
        const uniqueSelector = `[data-preview-id="${currentUniqueId}"]`
        // Remplacer TOUS les sélecteurs #custom-background-container de manière exhaustive
        let finalCss = cssText
          // Remplacer d'abord les cas avec espaces (sélecteurs descendants)
          .replace(/#custom-background-container\s+\./g, `${uniqueSelector} .`)
          .replace(/#custom-background-container\s+/g, `${uniqueSelector} `)
          // Puis les autres cas
          .replace(/#custom-background-container>/g, `${uniqueSelector} >`)
          .replace(/#custom-background-container\./g, `${uniqueSelector}.`)
          .replace(/#custom-background-container#/g, `${uniqueSelector}#`)
          .replace(/#custom-background-container\[/g, `${uniqueSelector}[`)
          .replace(/#custom-background-container:/g, `${uniqueSelector}:`)
          .replace(/#custom-background-container{/g, `${uniqueSelector} {`)
          .replace(/#custom-background-container,/g, `${uniqueSelector},`)
          .replace(/#custom-background-container$/gm, uniqueSelector)
          // Remplacer body par le sélecteur unique (pour shooting-stars)
          .replace(/^body\s*{/gm, `${uniqueSelector} {`)
          .replace(/\sbody\s*{/g, ` ${uniqueSelector} {`)
          // Remplacer * par le sélecteur unique (pour shooting-stars)
          .replace(/^\*\s*{/gm, `${uniqueSelector} * {`)
          .replace(/position:\s*fixed/g, 'position: absolute')
          .replace(/100vw/g, '100%')
          .replace(/100vh/g, '100%')
        
        // Ajouter un style de base pour le conteneur si nécessaire
        if (!finalCss.includes(uniqueSelector)) {
          finalCss = `${uniqueSelector} { position: absolute; top: 0; left: 0; width: 100%; height: 100%; overflow: hidden; background: #1a1e23; }\n${finalCss}`
        } else {
          // S'assurer que le conteneur a un fond
          finalCss = finalCss.replace(
            new RegExp(`(${uniqueSelector.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*{[^}]*)(})`, 'g'),
            (match, before, after) => {
              if (!before.includes('background:')) {
                return before + ' background: #1a1e23;' + after
              }
              return match
            }
          )
        }
        
        // Adaptations spécifiques pour bubble-float dans la prévisualisation
        if (backgroundId === 'bubble-float') {
          // Remplacer les unités vw par des pourcentages ou pixels pour la prévisualisation
          finalCss = finalCss
            .replace(/--bubble-radius:\s*(\d+(?:\.\d+)?)vw/g, (match, value) => {
              // Convertir vw en pixels approximatifs pour la prévisualisation (200px de largeur estimée)
              // Augmenter la taille minimale pour une meilleure visibilité
              const pixelValue = (parseFloat(value) / 100) * 200
              return `--bubble-radius: ${Math.max(8, pixelValue * 1.2)}px`
            })
            .replace(/--bubble-left-offset:\s*(\d+(?:\.\d+)?)vw/g, (match, value) => {
              // Convertir vw en pourcentage pour la prévisualisation
              return `--bubble-left-offset: ${value}%`
            })
            // S'assurer que .bubbles a la bonne hauteur
            .replace(new RegExp(`${uniqueSelector.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\.bubbles\\s*{[^}]*height:\\s*100vh`, 'g'), (match) => {
              return match.replace(/height:\s*100vh/, 'height: 100%')
            })
            // Commencer complètement en bas (hors écran) pour que les bulles apparaissent entièrement
            // Utiliser une valeur en pixels pour la prévisualisation (120px de hauteur)
            .replace(/bottom:\s*-75%/g, 'bottom: -120px')
            // Ajuster la distance de déplacement pour que les bulles traversent tout le conteneur
            // 120px (hauteur) + 120px (déplacement) = 240px total pour traverser complètement
            .replace(/translateY\(-175vh\)/g, 'translateY(-240px)')
            // Réduire la durée des animations pour accélérer le mouvement (0.3x plus rapide)
            .replace(/--bubble-float-duration:\s*(\d+(?:\.\d+)?)s/g, (match, value) => {
              return `--bubble-float-duration: ${parseFloat(value) * 0.3}s`
            })
            // Réduire les délais pour que les bulles apparaissent plus vite (10% du délai original)
            .replace(/--bubble-float-delay:\s*(\d+(?:\.\d+)?)s/g, (match, value) => {
              return `--bubble-float-delay: ${Math.max(0, parseFloat(value) * 0.1)}s`
            })
            // Augmenter l'opacité des bulles pour la prévisualisation
            .replace(/hsla\(183,\s*94%,\s*76%,\s*0\.3\)/g, 'hsla(183, 94%, 76%, 0.6)')
          
          // Ajouter un style de débogage pour forcer l'affichage et ajuster les positions
          finalCss += `
            ${uniqueSelector} .bubbles {
              position: relative !important;
              width: 100% !important;
              height: 100% !important;
              overflow: hidden !important;
              display: block !important;
              visibility: visible !important;
            }
            ${uniqueSelector} .bubble {
              display: block !important;
              visibility: visible !important;
              opacity: 1 !important;
            }
            ${uniqueSelector} .bubble::before {
              display: block !important;
              visibility: visible !important;
              opacity: 1 !important;
            }
          `
        }
        
        // Adaptations spécifiques pour shooting-stars dans la prévisualisation
        if (backgroundId === 'shooting-stars') {
          // Rendre le fond plus bleu (couleurs plus saturées en bleu)
          finalCss = finalCss
            .replace(/background:\s*linear-gradient\(to bottom,\s*#0b0b2b,\s*#1b2735\s+70%,\s*#090a0f\)/g, 
              'background: linear-gradient(to bottom, #0a0a4a, #1a2a5a 70%, #0a0a3a)')
            // Rendre les étoiles filantes plus bleues (bleu ciel)
            .replace(/background:\s*linear-gradient\(90deg,\s*white,\s*transparent\)/g,
              'background: linear-gradient(90deg, #87ceeb, transparent)')
            // Rendre les étoiles statiques légèrement bleues aussi
            .replace(/background:\s*white/g, '#b0e0e6')
        }
        
        // Adaptations spécifiques pour gradient-waves dans la prévisualisation
        if (backgroundId === 'gradient-waves') {
          // Augmenter la saturation et l'intensité des couleurs du gradient
          finalCss = finalCss
            // Remplacer le gradient assombri par un gradient plus coloré et visible
            .replace(/background:\s*linear-gradient\(315deg,\s*rgba\(20,0,18,0\.6\)\s+3%,\s*rgba\(12,26,41,0\.6\)\s+38%,\s*rgba\(9,47,45,0\.6\)\s+68%,\s*rgba\(51,5,5,0\.6\)\s+98%\)/g,
              'background: linear-gradient(315deg, rgba(150,0,130,1) 3%, rgba(50,120,220,1) 38%, rgba(40,200,180,1) 68%, rgba(255,50,50,1) 98%)')
            // Augmenter l'opacité des vagues
            .replace(/background:\s*rgb\(255\s+255\s+255\s+\/\s+8%\)/g, 'background: rgb(255 255 255 / 30%)')
            .replace(/opacity:\s*0\.3/g, 'opacity: 0.7')
            .replace(/opacity:\s*0\.25/g, 'opacity: 0.6')
            .replace(/opacity:\s*0\.2/g, 'opacity: 0.5')
          
          // Ajouter un style de débogage pour forcer l'affichage du gradient
          finalCss += `
            ${uniqueSelector} {
              background: linear-gradient(315deg, rgba(150,0,130,1) 3%, rgba(50,120,220,1) 38%, rgba(40,200,180,1) 68%, rgba(255,50,50,1) 98%) !important;
              background-size: 400% 400% !important;
              animation: gradient 15s ease infinite !important;
            }
          `
        }
        
        // Adaptations spécifiques pour day-sunset-night dans la prévisualisation
        if (backgroundId === 'day-sunset-night') {
          // S'assurer que l'image prend toute la place
          finalCss += `
            ${uniqueSelector} #background {
              width: 100% !important;
              height: 100% !important;
              object-fit: cover !important;
              position: absolute !important;
              top: 0 !important;
              left: 0 !important;
            }
          `
        }
        
        // Ajouter les imports de polices uniquement pour foil-stamping
        if (backgroundId === 'foil-stamping') {
          finalCss = `@import url("https://fonts.googleapis.com/css2?family=Noto+Sans+Symbols+2&display=swap");\n${finalCss}`
        }
        
        style.textContent = finalCss
        document.head.appendChild(style)
        styleRef.current = style
        
        // FORCER l'application du style en ajoutant un délai
        later(() => {
          if (previewContainerRef.current) {
            previewContainerRef.current.style.display = 'block'
            previewContainerRef.current.style.visibility = 'visible'
            previewContainerRef.current.style.opacity = '1'
          }
        }, 50)

        // Charger le HTML si disponible
        if (bgConfig.html) {
          const htmlPath = `/backgrounds/${bgConfig.folder}/${bgConfig.html}`
          fetch(htmlPath)
            .then(response => {
              if (!response.ok) throw new Error(`HTTP ${response.status}`)
              return response.text()
            })
            .then(htmlText => {
              // Vérifier à nouveau que le conteneur existe toujours
              if (!previewContainerRef.current || !container.contains(previewContainerRef.current)) {
                return
              }
              
              const parser = new DOMParser()
              const doc = parser.parseFromString(htmlText, 'text/html')
              // Pour gradient-waves, extraire le contenu du body ou utiliser directement
              let bodyContent = doc.body.innerHTML || htmlText
              
              // Si le HTML contient un body, extraire son contenu
              if (htmlText.includes('<body>')) {
                const bodyMatch = htmlText.match(/<body[^>]*>([\s\S]*?)<\/body>/i)
                if (bodyMatch) {
                  bodyContent = bodyMatch[1]
                }
              }
              
              // FORCER l'affichage en injectant directement le HTML
              previewContainer.innerHTML = bodyContent
              
              // FORCER la visibilité du conteneur
              previewContainer.style.display = 'block'
              previewContainer.style.visibility = 'visible'
              previewContainer.style.opacity = '1'
              
              // Pour bubble-float, s'assurer que .bubbles est visible
              if (backgroundId === 'bubble-float') {
                const bubblesElement = previewContainer.querySelector('.bubbles')
                if (bubblesElement) {
                  bubblesElement.style.position = 'relative'
                  bubblesElement.style.width = '100%'
                  bubblesElement.style.height = '100%'
                  bubblesElement.style.overflow = 'hidden'
                  bubblesElement.style.display = 'block'
                  bubblesElement.style.visibility = 'visible'
                  
                  // Forcer l'affichage des bulles
                  const bubbles = previewContainer.querySelectorAll('.bubble')
                  bubbles.forEach((bubble, index) => {
                    bubble.style.display = 'block'
                    bubble.style.visibility = 'visible'
                    bubble.style.opacity = '1'
                  })
                }
              }
              
              // Pour gradient-waves, s'assurer que le gradient est visible
              if (backgroundId === 'gradient-waves') {
                // Le gradient doit être sur le conteneur principal
                previewContainer.style.background = 'linear-gradient(315deg, rgba(150,0,130,1) 3%, rgba(50,120,220,1) 38%, rgba(40,200,180,1) 68%, rgba(255,50,50,1) 98%)'
                previewContainer.style.backgroundSize = '400% 400%'
                
                // S'assurer que les vagues sont visibles
                const waves = previewContainer.querySelectorAll('.wave')
                waves.forEach((wave) => {
                  wave.style.display = 'block'
                  wave.style.visibility = 'visible'
                })
              }
              
              // Pour day-sunset-night, s'assurer que l'image est visible
              if (backgroundId === 'day-sunset-night') {
                const bgImage = previewContainer.querySelector('#background')
                if (bgImage) {
                  bgImage.style.width = '100%'
                  bgImage.style.height = '100%'
                  bgImage.style.objectFit = 'cover'
                  bgImage.style.position = 'absolute'
                  bgImage.style.top = '0'
                  bgImage.style.left = '0'
                }
              }
              
              // Masquer l'indicateur de chargement
              const loadingIndicator = container.querySelector('.preview-loading-indicator')
              if (loadingIndicator) {
                loadingIndicator.style.display = 'none'
              }
              
              // Charger le JS après que le HTML soit injecté
              if (bgConfig.js) {
                const jsPath = `/backgrounds/${bgConfig.folder}/${bgConfig.js}`
                
                // Pour day-sunset-night, charger et adapter le script
                if (backgroundId === 'day-sunset-night') {
                  fetch(jsPath)
                    .then(response => response.text())
                    .then(jsText => {
                      // Adapter le script pour qu'il fonctionne dans le conteneur de prévisualisation
                      let adaptedJs = jsText
                        // Remplacer document.getElementById par une recherche dans le conteneur
                        .replace(
                          /var image = document\.getElementById\('background'\);/g,
                          `var previewContainer = document.querySelector('[data-preview-id="${currentUniqueId}"]'); var image = previewContainer ? previewContainer.querySelector('#background') : null; if (!image) { return; }`
                        )
                        // S'assurer que load() s'exécute après l'injection
                        .replace(
                          /if \(document\.readyState === 'loading'\)/g,
                          'if (false && document.readyState === \'loading\')'
                        )
                        .replace(
                          /setTimeout\(load, 100\);/g,
                          'setTimeout(load, 300);'
                        )
                      
                      // Exécuter le script adapté
                      const script = document.createElement('script')
                      script.textContent = adaptedJs
                      later(() => {
                        if (cancelled) return
                        document.head.appendChild(script)
                        scriptRef.current = script
                        later(() => {
                          if (cancelled) return
                          if (typeof load === 'function') {
                            try { load() } catch { /* nœud preview déjà retiré */ }
                          }
                        }, 400)
                      }, 200)
                    })
                    .catch(err => {
                      console.error('Erreur chargement JS preview:', err)
                    })
                } else {
                  // Pour les autres backgrounds, charger normalement
                  const script = document.createElement('script')
                  script.src = jsPath
                  script.onerror = () => console.error('Erreur chargement JS preview')
                  later(() => {
                    document.head.appendChild(script)
                    scriptRef.current = script
                  }, 100)
                }
              }
            })
            .catch(err => {
              console.error('Erreur chargement HTML preview:', err)
              // Afficher un message d'erreur visible
              if (previewContainerRef.current) {
                previewContainerRef.current.innerHTML = `<div style="color: rgba(255,255,255,0.7); padding: 20px; text-align: center;">Erreur: ${err.message}</div>`
              }
              const loadingIndicator = container.querySelector('.preview-loading-indicator')
              if (loadingIndicator) {
                loadingIndicator.textContent = 'Erreur'
                loadingIndicator.style.color = 'rgba(255, 0, 0, 0.7)'
              }
            })
        } else if (bgConfig.js) {
          const jsPath = `/backgrounds/${bgConfig.folder}/${bgConfig.js}`
          const script = document.createElement('script')
          script.src = jsPath
          script.onerror = () => console.error('Erreur chargement JS preview')
          document.head.appendChild(script)
          scriptRef.current = script
        } else {
          // Si pas de HTML ni JS, masquer l'indicateur quand même
          later(() => {
            const loadingIndicator = container.querySelector('.preview-loading-indicator')
            if (loadingIndicator) {
              loadingIndicator.style.display = 'none'
            }
          }, 500)
        }
      })
      .catch(err => {
        console.error('Erreur chargement CSS preview:', err)
        // Si le CSS n'existe pas mais qu'on a du HTML, charger directement le HTML
        if (!bgConfig.css && bgConfig.html) {
          loadHTMLAndExtractCSS()
        } else {
          const loadingIndicator = container.querySelector('.preview-loading-indicator')
          if (loadingIndicator) {
            loadingIndicator.textContent = 'Erreur de chargement'
            loadingIndicator.style.color = 'rgba(255, 0, 0, 0.7)'
          }
        }
      })
    } else {
      // Pas de CSS séparé, charger directement le HTML (qui peut contenir du CSS intégré)
      loadHTMLAndExtractCSS()
    }

    return () => {
      cancelled = true
      timeouts.forEach((id) => window.clearTimeout(id))
      if (typeof window !== 'undefined' && window.daySunsetNightInterval) {
        window.clearInterval(window.daySunsetNightInterval)
        window.daySunsetNightInterval = null
      }
      if (styleRef.current) {
        styleRef.current.remove()
        styleRef.current = null
      }
      if (scriptRef.current) {
        scriptRef.current.remove()
        scriptRef.current = null
      }
      if (previewContainerRef.current) {
        previewContainerRef.current.remove()
        previewContainerRef.current = null
      }
      if (container) {
        container.innerHTML = ''
      }
    }
  }, [backgroundId])

  return (
    <div ref={containerRef} className="background-preview-wrapper">
      {/* Indicateur de chargement */}
      <div 
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'rgba(255, 255, 255, 0.5)',
          fontSize: '12px',
          pointerEvents: 'none',
          zIndex: 1
        }}
        className="preview-loading-indicator"
      >
        Chargement...
      </div>
    </div>
  )
}

export default BackgroundPreview

